import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { HomeQuickAction, HomeQuickActionId } from './buildHomeQuickActions';

type ActionLook = { tone: string; icon: ReactNode };

const LOOKS: Record<HomeQuickActionId, ActionLook> = {
  'create-playlist': {
    tone: '#D6455D',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3.5" y="3.5" width="17" height="17" rx="2.5" />
        <circle cx="9" cy="9" r="1.8" />
        <path d="m20 15-4.5-4.5L6 20" />
      </svg>
    ),
  },
  'request-design': {
    tone: '#7A55D6',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
      </svg>
    ),
  },
  'assign-screens': {
    tone: '#2E9E4F',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
        <rect x="3" y="4" width="18" height="12" rx="2" />
        <path d="M9 20h6M12 16v4" />
      </svg>
    ),
  },
  schedule: {
    tone: '#C98A10',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
        <rect x="3.5" y="5" width="17" height="15" rx="2" />
        <path d="M3.5 10h17M8 3v4M16 3v4" />
      </svg>
    ),
  },
};

function tint(tone: string, percent: number): string {
  return `color-mix(in oklch, ${tone} ${percent}%, var(--gs-panel))`;
}

type HomeQuickActionsProps = {
  actions: HomeQuickAction[];
};

export function HomeQuickActions({ actions }: HomeQuickActionsProps) {
  if (actions.length === 0) return null;

  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,230px),1fr))] gap-3.5">
      {actions.map((action) => {
        const look = LOOKS[action.id];
        return (
          <Link
            key={action.id}
            to={action.to}
            className="flex items-center gap-3.5 rounded-[14px] border p-4 transition hover:border-[var(--gs-accent)]"
            style={{
              background: tint(look.tone, 5),
              borderColor: 'var(--gs-line)',
              color: 'var(--gs-ink)',
            }}
          >
            <span
              className="grid h-11 w-11 shrink-0 place-items-center rounded-[11px]"
              style={{ background: tint(look.tone, 14), color: look.tone }}
              aria-hidden
            >
              {look.icon}
            </span>
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-[15px] font-bold">{action.title}</span>
              <span
                className="truncate text-[13px]"
                style={{
                  color: action.highlighted ? 'var(--gs-accent)' : 'var(--gs-muted)',
                  fontWeight: action.highlighted ? 600 : undefined,
                }}
              >
                {action.hint}
              </span>
            </span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              className="shrink-0 rtl:-scale-x-100"
              style={{ color: 'var(--gs-muted)' }}
              aria-hidden
            >
              <path d="m9 6 6 6-6 6" />
            </svg>
          </Link>
        );
      })}
    </div>
  );
}
